import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Circle, ChevronRight, Loader2 } from 'lucide-react';

import type { ExecutionSummaryV2 } from '../../lib/types';
import { getExecutionTimestamp, getExecutionDuration } from '../../lib/execution-helpers';
import { cn } from '../../lib/utils';

const FLOW_ERROR_MESSAGES: Record<string, string> = {
  PROJECT_ACCESS_DENIED: "You don't have access to this project",
  AUTH_REQUIRED: 'Authentication is required',
  PROJECT_NOT_FOUND: 'Project not found',
};

interface ApiErrorResponse {
  code?: string;
  message?: string;
}

// ─── Data fetching ────────────────────────────────────────────────────────────

async function fetchRelatedExecutions(
  projectId: string,
  relatedRequestId: string,
): Promise<ExecutionSummaryV2[]> {
  const url = `/api/projects/${projectId}/executions?relatedRequestId=${encodeURIComponent(relatedRequestId)}&limit=100`;
  const res = await fetch(url);
  if (!res.ok) {
    const errorBody = (await res.json().catch(() => null)) as ApiErrorResponse | null;
    const code = errorBody?.code;
    if (code != null) {
      const friendly = FLOW_ERROR_MESSAGES[code] ?? 'Failed to load request flow';
      throw new Error(`${friendly} (${code})`);
    }
    throw new Error(errorBody?.message ?? `Failed to load request flow (${res.status})`);
  }
  const data = (await res.json()) as { executions?: ExecutionSummaryV2[] };
  return data.executions ?? [];
}

// ─── Tree construction ────────────────────────────────────────────────────────

interface TreeNode {
  exec: ExecutionSummaryV2;
  children: TreeNode[];
  depth: number;
}

function buildTree(executions: ExecutionSummaryV2[]): TreeNode[] {
  const byRequestId = new Map<string, ExecutionSummaryV2>();
  for (const exec of executions) {
    if (exec.requestId != null) byRequestId.set(exec.requestId, exec);
  }

  // Roots: executions whose parentRequestId is absent or doesn't appear in our set
  const roots = executions.filter(
    (e): boolean => e.parentRequestId == null || !byRequestId.has(e.parentRequestId),
  );

  function buildNode(exec: ExecutionSummaryV2, depth: number): TreeNode {
    const children = executions
      .filter(
        (e): boolean => e.requestId !== exec.requestId && e.parentRequestId === exec.requestId,
      )
      .sort(
        (a, b): number =>
          Date.parse(getExecutionTimestamp(a)) - Date.parse(getExecutionTimestamp(b)),
      )
      .map((e): TreeNode => buildNode(e, depth + 1));
    return { exec, children, depth };
  }

  return roots
    .sort(
      (a, b): number => Date.parse(getExecutionTimestamp(a)) - Date.parse(getExecutionTimestamp(b)),
    )
    .map((r): TreeNode => buildNode(r, 0));
}

// ─── Single tree node ─────────────────────────────────────────────────────────

function FlowNode({
  node,
  currentExecutionId,
  isReplaying,
  projectId,
  onNavigate,
}: {
  node: TreeNode;
  currentExecutionId: string;
  isReplaying: boolean;
  projectId: string;
  onNavigate: (executionId: string) => void;
}): import('react/jsx-runtime').JSX.Element {
  const isCurrent = node.exec.executionId === currentExecutionId;
  const isError = node.exec.status === 'error';

  return (
    <div>
      <button
        onClick={(): false | void => !isCurrent && onNavigate(node.exec.executionId)}
        disabled={isCurrent}
        className={cn(
          'w-full text-left flex items-start gap-2 rounded py-1.5 pr-2 text-xs transition-colors',
          isCurrent ? 'bg-muted/60 cursor-default' : 'hover:bg-muted/40 cursor-pointer group',
        )}
        style={{ paddingLeft: `${8 + node.depth * 14}px` }}
      >
        {/* Indent connector */}
        {node.depth > 0 && (
          <ChevronRight className="h-2.5 w-2.5 shrink-0 mt-0.5 text-muted-foreground/30" />
        )}

        {/* Status dot — pulses on current if replaying */}
        <Circle
          className={cn(
            'h-2 w-2 shrink-0 mt-0.5 fill-current',
            isError ? 'text-[var(--flux-error)]' : 'text-[var(--flux-success)]',
            isCurrent && isReplaying && 'animate-pulse',
          )}
        />

        <div className="min-w-0 flex-1">
          <div
            className={cn(
              'truncate font-medium leading-tight',
              isCurrent
                ? 'text-foreground'
                : 'text-muted-foreground group-hover:text-foreground transition-colors',
            )}
          >
            {node.exec.entry ??
              ((node.exec as unknown as Record<string, unknown>)['name'] as string | undefined) ??
              'unnamed'}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className={cn(
                'font-mono tabular-nums',
                getExecutionDuration(node.exec) > 500
                  ? 'text-[var(--flux-error)]/80'
                  : 'text-muted-foreground/50',
              )}
              style={{ fontSize: '10px' }}
            >
              {getExecutionDuration(node.exec)}ms
            </span>
            {getExecutionDuration(node.exec) > 500 && (
              <span className="text-[8px] font-bold font-mono text-[var(--flux-error)] bg-[var(--flux-error)]/10 px-1 py-px rounded uppercase">
                Slow
              </span>
            )}
          </div>
        </div>

        {isCurrent && (
          <span
            className="shrink-0 uppercase tracking-wide text-[var(--flux-accent)] mt-0.5"
            style={{ fontSize: '9px' }}
          >
            here
          </span>
        )}
      </button>

      {node.children.map((child): import('react/jsx-runtime').JSX.Element => (
        <FlowNode
          key={child.exec.executionId}
          node={child}
          currentExecutionId={currentExecutionId}
          isReplaying={isReplaying}
          projectId={projectId}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

// ─── Standalone single-node stub ──────────────────────────────────────────────

function StandaloneNode({
  isError,
}: {
  isError: boolean;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-muted/60 text-xs mx-1">
      <Circle
        className={cn(
          'h-2 w-2 shrink-0 fill-current',
          isError ? 'text-[var(--flux-error)]' : 'text-[var(--flux-success)]',
        )}
      />
      <span className="truncate text-foreground flex-1">this execution</span>
      <span
        className="shrink-0 uppercase tracking-wide text-[var(--flux-accent)]"
        style={{ fontSize: '9px' }}
      >
        here
      </span>
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export interface FlowPanelProps {
  projectId: string;
  executionId: string;
  requestId?: string | undefined;
  parentRequestId?: string | undefined;
  status?: 'success' | 'error' | undefined;
  /** When true, the current execution node pulses — used during active replay. */
  isReplaying?: boolean;
  /**
   * Optional static related-executions payload for read-only embeds (e.g. marketing).
   * When provided, the panel skips API fetching and renders from this list.
   */
  staticExecutions?: ExecutionSummaryV2[] | undefined;
  /**
   * Optional navigation override used by non-app contexts where /projects routes
   * are not available.
   */
  onNavigateExecution?: ((executionId: string) => void) | undefined;
}

function FlowPanelBody({
  requestId,
  status,
  isLoading,
  error,
  tree,
  totalCount,
  executionId,
  isReplaying,
  projectId,
  onNavigate,
}: {
  requestId?: string | undefined;
  status: 'success' | 'error';
  isLoading: boolean;
  error: Error | null;
  tree: TreeNode[];
  totalCount: number;
  executionId: string;
  isReplaying: boolean;
  projectId: string;
  onNavigate: (executionId: string) => void;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="w-52 shrink-0 border-l flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2.5 border-b shrink-0">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground">Request Flow</p>
          {totalCount > 1 && (
            <span className="text-muted-foreground/40" style={{ fontSize: '10px' }}>
              {totalCount}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto py-2">
        {requestId == null ? (
          // No requestId — show standalone node + explanation
          <div className="space-y-2">
            <StandaloneNode isError={status === 'error'} />
            <p
              className="text-muted-foreground/40 px-3 leading-relaxed"
              style={{ fontSize: '10px' }}
            >
              Standalone — no request ID.
            </p>
          </div>
        ) : isLoading ? (
          <div
            className="flex items-center gap-2 px-3 py-4 text-muted-foreground/40"
            style={{ fontSize: '10px' }}
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading flow…
          </div>
        ) : error ? (
          <p
            className="text-[10px] text-[var(--flux-error)]/80 px-3 leading-relaxed"
            data-testid="execution-flow-error"
          >
            {error.message}
          </p>
        ) : (
          <div className="space-y-0.5">
            {tree.map((node): import('react/jsx-runtime').JSX.Element => (
              <FlowNode
                key={node.exec.executionId}
                node={node}
                currentExecutionId={executionId}
                isReplaying={isReplaying}
                projectId={projectId}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FlowPanelFetched({
  projectId,
  executionId,
  requestId,
  parentRequestId,
  status,
  isReplaying,
  onNavigate,
}: {
  projectId: string;
  executionId: string;
  requestId?: string | undefined;
  parentRequestId?: string | undefined;
  status: 'success' | 'error';
  isReplaying: boolean;
  onNavigate: (executionId: string) => void;
}): import('react/jsx-runtime').JSX.Element {
  const rootRequestId = parentRequestId ?? requestId;

  const {
    data: related,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['flow-related', projectId, rootRequestId],
    enabled: true,
    queryFn: async (): Promise<ExecutionSummaryV2[]> => {
      const seen = new Set<string>();
      const results: ExecutionSummaryV2[] = [];

      const addAll = (execs: ExecutionSummaryV2[]): void => {
        for (const e of execs) {
          if (!seen.has(e.executionId)) {
            seen.add(e.executionId);
            results.push(e);
          }
        }
      };

      // Always root the tree at the top of the chain.
      // - If this execution has a parentRequestId, that IS the root's own requestId.
      // - If not, this execution IS the root — use its own requestId.
      if (rootRequestId != null) {
        addAll(await fetchRelatedExecutions(projectId, rootRequestId));
      }

      // Always ensure the current execution appears (may not be in hot window)
      if (!seen.has(executionId)) {
        results.unshift({
          executionId,
          projectId,
          receivedAt: new Date().toISOString(),
          entry: null,
          status: status,
          startedAt: new Date().toISOString(),
          completedAt: null,
          durationMs: 0,
          eventCount: null,
          requestId: requestId ?? null,
          parentRequestId: parentRequestId ?? null,
          traceId: null,
          preview: null,
          meta: null,
          error: null,
        } as unknown as ExecutionSummaryV2);
      }

      return results;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const tree = related && related.length > 0 ? buildTree(related) : [];
  const totalCount = related?.length ?? 0;

  return (
    <FlowPanelBody
      requestId={requestId}
      status={status}
      isLoading={isLoading}
      error={error}
      tree={tree}
      totalCount={totalCount}
      executionId={executionId}
      isReplaying={isReplaying}
      projectId={projectId}
      onNavigate={onNavigate}
    />
  );
}

export function FlowPanel({
  projectId,
  executionId,
  requestId,
  parentRequestId,
  status = 'success',
  isReplaying = false,
  staticExecutions,
  onNavigateExecution,
}: FlowPanelProps): import('react/jsx-runtime').JSX.Element {
  const navigate = useNavigate();
  const handleNavigate = (targetId: string): void => {
    if (onNavigateExecution) {
      onNavigateExecution(targetId);
      return;
    }
    navigate(`/projects/${projectId}/executions/${targetId}`);
  };

  if (staticExecutions != null) {
    const tree = staticExecutions.length > 0 ? buildTree(staticExecutions) : [];
    return (
      <FlowPanelBody
        requestId={requestId}
        status={status}
        isLoading={false}
        error={null}
        tree={tree}
        totalCount={staticExecutions.length}
        executionId={executionId}
        isReplaying={isReplaying}
        projectId={projectId}
        onNavigate={handleNavigate}
      />
    );
  }

  return (
    <FlowPanelFetched
      projectId={projectId}
      executionId={executionId}
      requestId={requestId}
      parentRequestId={parentRequestId}
      status={status}
      isReplaying={isReplaying}
      onNavigate={handleNavigate}
    />
  );
}
